import type { LucideIcon } from "lucide-react";
import {
  Bell,
  CreditCard,
  Key,
  Link2,
  Lock,
  Settings,
  Users,
  UserCircle,
  Webhook
} from "lucide-react";
import type { WorkspaceRole } from "../hooks/useAdminMe.js";
import type { AccessManifestCan } from "../hooks/useAccessManifest.js";
import { roleAtLeast } from "../hooks/useCurrentRole.js";

export type SettingsNavItem = {
  to: string;
  label: string;
  icon: LucideIcon;
  minRole?: WorkspaceRole;
  manifestCan?: AccessManifestCan;
};

export type SettingsNavGroup = {
  id: string;
  label: string;
  items: SettingsNavItem[];
};

export const settingsNavGroups: SettingsNavGroup[] = [
  {
    id: "personal",
    label: "Personal",
    items: [
      {
        to: "/settings/account",
        label: "Account",
        icon: UserCircle
      },
      {
        to: "/settings/notifications",
        label: "Notifications",
        icon: Bell
      }
    ]
  },
  {
    id: "workspace",
    label: "Workspace",
    items: [
      {
        to: "/settings/workspace",
        label: "General",
        icon: Settings
      }
    ]
  },
  {
    id: "team",
    label: "Team & access",
    items: [
      {
        to: "/settings/members",
        label: "Members",
        icon: Users,
        minRole: "viewer"
      },
      {
        to: "/settings/access",
        label: "Roles & permissions",
        icon: Lock
      },
      {
        to: "/settings/approver-groups",
        label: "Approver groups",
        icon: Webhook,
        minRole: "viewer"
      }
    ]
  },
  {
    id: "developers",
    label: "Developers",
    items: [
      {
        to: "/settings/api-keys",
        label: "API keys",
        icon: Key,
        manifestCan: "view_api_keys_meta"
      },
      {
        to: "/settings/connectors",
        label: "Connectors",
        icon: Link2,
        manifestCan: "view_connectors"
      }
    ]
  },
  {
    id: "billing",
    label: "Billing",
    items: [
      {
        to: "/settings/billing",
        label: "Plan & usage",
        icon: CreditCard,
        minRole: "owner"
      }
    ]
  }
];

export const filterSettingsNavGroups = (input: {
  role: WorkspaceRole | null;
  manifestCan: Record<string, boolean> | undefined;
}): SettingsNavGroup[] => {
  return settingsNavGroups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => {
        if (item.minRole !== undefined && !roleAtLeast(input.role, item.minRole)) {
          return false;
        }
        if (item.manifestCan !== undefined) {
          if (input.manifestCan?.[item.manifestCan] !== true) {
            return false;
          }
        }
        return true;
      })
    }))
    .filter((group) => group.items.length > 0);
};
